import { Popover, PopoverContent, PopoverTrigger, Tooltip } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import {
  AddCircleFilledIcon,
  CustomDateIcon,
  CustomEmailIcon,
  CustomMultiselectIcon,
  CustomNumberIcon,
  CustomPersonIcon,
  CustomPhoneIcon,
  CustomSelectIcon,
  CustomTextIcon,
  CustomUrlIcon,
  DeleteIcon,
  DragIcon,
  DuplicateIcon,
  LockedIcon,
} from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { PLANRULES } from 'src/constants/plans';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { useAuth } from 'src/shared/hooks/use-auth';
import { Member } from 'src/type';
import { v4 as uuid } from 'uuid';

import { useTasksStore } from '../store/useTasksStore';
import { CustomFieldType, Task, TypeEnum } from '../types/tasks';
import { DeleteField } from './DeleteField';
import {
  DateFieldComponent,
  EmailFieldComponent,
  MultiselectFieldComponent,
  NumberFieldComponent,
  PersonFieldComponent,
  PhoneFieldComponent,
  SelectFieldComponent,
  TextFieldComponent,
  UrlFieldComponent,
} from './TaskFieldComponents';

const getInputComponent = (type: string) => {
  switch (type) {
    case 'text':
      return TextFieldComponent;
    case 'number':
      return NumberFieldComponent;
    case 'date':
      return DateFieldComponent;
    case 'select':
      return SelectFieldComponent;
    case 'multiselect':
      return MultiselectFieldComponent;
    case 'person':
      return PersonFieldComponent;
    case 'url':
      return UrlFieldComponent;
    case 'email':
      return EmailFieldComponent;
    case 'phone':
      return PhoneFieldComponent;
    default:
      return TextFieldComponent;
  }
};

const getFieldIcon = (type: string) => {
  switch (type) {
    case 'text':
      return CustomTextIcon;
    case 'number':
      return CustomNumberIcon;
    case 'date':
      return CustomDateIcon;
    case 'select':
      return CustomSelectIcon;
    case 'multiselect':
      return CustomMultiselectIcon;
    case 'person':
      return CustomPersonIcon;
    case 'url':
      return CustomUrlIcon;
    case 'email':
      return CustomEmailIcon;
    case 'phone':
      return CustomPhoneIcon;
    default:
      return CustomTextIcon;
  }
};

const fieldOptionLabels = {
  text: 'Text',
  number: 'Number',
  select: 'Select',
  multiselect: 'Multi-select',
  date: 'Date',
  person: 'Person',
  url: 'URL',
  email: 'Email',
  phone: 'Phone',
};

export const TaskFields = ({
  task,
  setUnsetTemplateFields,
  members,
}: {
  task?: Partial<Task>;
  setUnsetTemplateFields?: (item: CustomFieldType) => void;
  members?: Member[];
}) => {
  const authData = useAuth((s) => s.userDetails);

  const onAddCustomField = useTasksStore((s) => s.onAddCustomField);
  const onAddCustomTemplateField = useTasksStore((s) => s.onAddCustomTemplateField);
  const onReorderCustomField = useTasksStore((s) => s.onReorderCustomField);
  const [open] = useSnackbar();
  const fields = useTasksStore((s) => s.tasks[task?.id || '']?.fields);

  const onAddNewField = async ({ type, name, data }) => {
    if (task?.id) {
      const payload = {
        taskId: task.id,
        type: type,
        name: name,
        data: data,
        index: fields?.length || 0,
      };
      let success = false;
      if (task.board?.id) {
        payload['boardId'] = task.board?.id;
        success = await onAddCustomField(payload);
      } else {
        // This case is to handle task template
        payload['taskTemplateRef'] = task.id;
        const fieldId = uuid();
        payload['fieldId'] = fieldId;
        const payloadToSend = { taskTemplateRef: task.id, fieldId: fieldId, ...payload };
        success = await onAddCustomTemplateField(payloadToSend);
        //set the newly added field to template fields to render the task template edit modal
        if (success)
          setUnsetTemplateFields?.({
            fieldId,
            type,
            label: name,
            data,
            index: fields?.length || 0,
          });
      }
      if (!success) {
        open({
          text: 'Failed to add field',
          snackType: 'error',
          duration: 3000,
          position: 'top-center',
        });
      }
    }
  };

  const allowCustomFields = authData?.plan.type
    ? !PLANRULES.customFields[authData?.plan.type]
    : false;

  const handleFieldDrop = async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;
    const oldIndex = source.index;
    const newIndex = destination.index;

    if (task?.id) {
      const payload = {
        taskId: task.id,
        newIndex,
        oldIndex,
      };
      let success = false;
      if (task.board?.id) {
        success = await onReorderCustomField(payload);
      } else {
        payload['taskTemplateRef'] = task.id;
        success = await onReorderCustomField(payload);
      }
      if (!success) {
        open({
          text: 'Failed to reorder fields',
          snackType: 'error',
          duration: 3000,
          position: 'top-center',
        });
      }
    }
  };

  const { isReadOnly } = useTaskAccess(task?.id || '');
  const { isReadOnly: isFileReadOnly } = useFileAccess(task?.fileId);

  return (
    <div>
      <div className="">
        <DragDropContext onDragEnd={handleFieldDrop}>
          <Droppable droppableId="task-fields">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {fields?.map(
                  (customField, index) =>
                    !!task && (
                      <Draggable
                        isDragDisabled={isReadOnly}
                        draggableId={customField.fieldId}
                        index={index}
                        key={customField.fieldId}
                      >
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <TaskFieldFactory
                              isReadOnly={isReadOnly}
                              task={task}
                              customField={customField}
                              setUnsetTemplateFields={setUnsetTemplateFields}
                              onAddNewField={onAddNewField}
                              dragHandleProps={provided.dragHandleProps}
                              members={members}
                            />
                          </div>
                        )}
                      </Draggable>
                    )
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {!isFileReadOnly && (
          <CreateField task={task} onAddNewField={onAddNewField} isDisabled={allowCustomFields} />
        )}
      </div>
    </div>
  );
};

const TaskFieldFactory = ({
  task,
  customField,
  setUnsetTemplateFields,
  onAddNewField: handleDuplicateField,
  dragHandleProps,
  members,
  isReadOnly,
}: {
  task: Partial<Task>;
  customField: CustomFieldType;
  setUnsetTemplateFields?: (item: CustomFieldType, remove: boolean) => void;
  onAddNewField: ({ type, name, data }: { type: TypeEnum; name?: string; data: any }) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  members?: Member[];
  isReadOnly?: boolean;
}): JSX.Element => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const onDeleteCustomField = useTasksStore((s) => s.onDeleteCustomField);
  const onDeleteCustomTemplateField = useTasksStore((s) => s.onDeleteCustomTemplateField);
  const [open] = useSnackbar();

  const handleDelete = async (flag?: boolean) => {
    if (flag) {
      if (task.id) {
        const payload = { fieldId: customField.fieldId, taskId: task.id };
        let success = false;
        if (task.board?.id === undefined) {
          payload['taskTemplateRef'] = task.id;
          success = await onDeleteCustomTemplateField(payload);
          //unset the deleted field to template fields to render the task template edit modal
          if (success) setUnsetTemplateFields?.(customField, true);
        } else {
          payload['taskTemplateRef'] = undefined;
          success = await onDeleteCustomField(payload);
        }
        if (!success) {
          open({
            text: 'Failed to delete field',
            snackType: 'error',
            duration: 3000,
            position: 'top-center',
          });
        }
      }
      setIsDeleteModalOpen(false);
    } else {
      setIsDeleteModalOpen(true);
    }
  };

  const InputComponent = useMemo(() => getInputComponent(customField.type), [customField.type]);

  return (
    <>
      <div className="flex py-2 gap-12 items-center group">
        <div className="flex items-center cursor-pointer text-gray-999">
          <CustomFieldLabel
            onDeleteField={handleDelete}
            onDuplicateField={handleDuplicateField}
            customField={customField}
            task={task}
            dragHandleProps={dragHandleProps}
          />
        </div>
        <InputComponent
          disabled={isReadOnly}
          task={task}
          key={customField.fieldId}
          fieldId={customField.fieldId}
          label={customField.label}
          data={customField.data}
          handleDelete={handleDelete}
          {...(customField?.type === 'person' ? { members } : {})}
        />
      </div>
      {isDeleteModalOpen && (
        <DeleteField
          handleDeleteField={handleDelete}
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
        />
      )}
    </>
  );
};

const CreateField = ({
  onAddNewField,
  isDisabled,
}: {
  task?: Partial<Task>;
  type?: TypeEnum;
  onAddNewField: ({
    type,
    name,
    data,
    taskTemplateRef,
  }: {
    type: TypeEnum;
    name?: string;
    data: any;
    taskTemplateRef?: string;
  }) => void;
  isDisabled: boolean;
}): JSX.Element => {
  const [label, setLabel] = useState('');
  const labelInputRef = useRef<HTMLInputElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(0);

  const fieldTypeOptions = Object.keys(fieldOptionLabels);
  const selectedType = fieldTypeOptions[selectedTypeIndex];

  const handleAddField = (type: string) => {
    switch (type) {
      case 'text':
      case 'number':
      case 'date':
      case 'select':
      case 'multiselect':
      case 'person':
      case 'url':
      case 'email':
      case 'phone':
        onAddNewField({ type: type, name: label || fieldOptionLabels[type], data: '' });
        break;
      default:
        break;
    }
    setShowPopup(false);
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    const type = selectedType as TypeEnum;
    onAddNewField({ type: type, name: label || fieldOptionLabels[type], data: '' });
    setShowPopup(false);
  };

  useEffect(() => {
    setLabel('');
    setSelectedTypeIndex(0);
  }, [showPopup]);

  useEffect(() => {
    if (showPopup) {
      setTimeout(() => {
        labelInputRef?.current?.focus();
      }, 100);
    }
  }, [showPopup, labelInputRef.current]);

  return (
    <Popover placement="bottom-start" isOpen={showPopup} onClose={() => setShowPopup(false)}>
      <PopoverTrigger>
        <Tippy
          disabled={!isDisabled}
          content={'This feature is locked in your current plan. Upgrade to Pro to unlock.'}
        >
          <button
            className="font-medium text-sm my-12 text-gray-500"
            onClick={() => !isDisabled && setShowPopup(true)}
          >
            <span className="flex items-center gap-8">
              <AddCircleFilledIcon size={20} className="text-gray-500" />
              <span className="text-gray-500">Add property</span>
              {isDisabled && <LockedIcon className="ml-8 text-gray-500" />}
            </span>
          </button>
        </Tippy>
      </PopoverTrigger>
      <PopoverContent>
        <div className="bg-white border rounded-lg">
          <div className="px-16 py-8 border-b text-sm font-medium">
            <form onSubmit={handleFormSubmit}>
              <input
                ref={labelInputRef}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={fieldOptionLabels[selectedType]}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown')
                    setSelectedTypeIndex((p) => (p === fieldTypeOptions.length - 1 ? 0 : p + 1));
                  else if (e.key === 'ArrowUp')
                    setSelectedTypeIndex((p) => (p > 0 ? p - 1 : fieldTypeOptions.length - 1));
                }}
              />
            </form>
          </div>
          <div className="p-4">
            <SelectCustomFieldTypeOptions
              selectedType={selectedType}
              onOptionSelect={handleAddField}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CustomFieldLabel = ({
  customField,
  task,
  onDuplicateField,
  onDeleteField,
  dragHandleProps,
}: {
  customField: CustomFieldType;
  task: Partial<Task>;
  onDeleteField: () => void;
  onDuplicateField: ({ type, name, data }: { type: TypeEnum; name?: string; data: any }) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps;
}) => {
  const [label, setLabel] = useState(customField.label);
  const labelInputRef = useRef<HTMLInputElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [open] = useSnackbar();

  const { isReadOnly: isFileReadOnly } = useFileAccess(task.fileId);

  const onUpdateCustomField = useTasksStore((s) => s.onUpdateCustomFieldType);
  const onUpdateCustomFieldName = useTasksStore((s) => s.onUpdateCustomFieldName);
  const onUpdateCustomTemplateField = useTasksStore((s) => s.onUpdateCustomTemplateFieldType);
  const onUpdateCustomTemplateFieldName = useTasksStore((s) => s.onUpdateCustomTemplateFieldName);

  const FieldIcon = getFieldIcon(customField.type);

  const updateFieldLabel = async (labelStr: string) => {
    if (task.id) {
      const payload = { taskId: task.id, fieldId: customField.fieldId, name: labelStr };
      let success = false;
      if (task.board?.id === undefined) {
        payload['taskTemplateRef'] = task.id;
        success = await onUpdateCustomTemplateFieldName(payload);
      } else {
        payload['taskTemplateRef'] = undefined;
        success = await onUpdateCustomFieldName(payload);
      }
      if (!success) {
        open({
          text: 'Failed to update field label',
          snackType: 'error',
          duration: 3000,
          position: 'top-center',
        });
      }
    }
  };

  const handleBasicMenu = (op: string) => {
    if (op === 'delete') {
      onDeleteField();
    }
    if (op === 'duplicate') {
      onDuplicateField({ type: customField.type, name: label, data: '' });
    }
    setShowPopup(false);
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    setShowPopup(false);
  };

  useEffect(() => {
    setLabel(customField.label);
  }, [customField.label]);

  useEffect(() => {
    if (showPopup && labelInputRef.current)
      setTimeout(() => {
        labelInputRef.current?.focus();
      }, 100);
  }, [showPopup, labelInputRef]);

  /** UPDATE FIELD LABEL ON POPUP CLOSE */
  useEffect(() => {
    if (!showPopup) {
      if (label && label !== customField.label) {
        updateFieldLabel(label);
      }
      setShowTypeMenu(false);
    }
  }, [showPopup]);

  const handleChangeFieldType = (type: TypeEnum) => {
    if (task.id) {
      const payload = {
        taskId: task.id,
        fieldId: customField.fieldId,
        type: type,
        name: label,
      };
      if (task.board?.id === undefined) {
        payload['taskTemplateRef'] = task.id;
        onUpdateCustomTemplateField(payload);
      } else {
        payload['taskTemplateRef'] = undefined;
        onUpdateCustomField(payload);
      }
    }
    setShowPopup(false);
  };

  return (
    <Popover
      isOpen={!isFileReadOnly && showPopup}
      onClose={() => setShowPopup(false)}
      placement="bottom-start"
      offset={[-15, -26]}
      preventOverflow={false}
      closeOnEsc
    >
      <PopoverTrigger>
        <div className="relative flex items-center">
          {!isFileReadOnly && (
            <span
              className="flex items-center absolute opacity-0 group-hover:opacity-100"
              {...dragHandleProps}
              style={{ left: -20, top: 4 }}
            >
              <DragIcon size={16} />
            </span>
          )}
          <button
            disabled={isFileReadOnly}
            onClick={() => setShowPopup(true)}
            className="flex items-center gap-10"
          >
            <FieldIcon />
            {customField.label}
          </button>
        </div>
      </PopoverTrigger>
      <PopoverContent>
        <div className="bg-white border rounded-lg">
          <div className="px-4 py-4 border-b text-sm font-medium text-gray-999">
            <form onSubmit={handleFormSubmit} className="flex items-center gap-8">
              <div
                className={cn('flex items-center border rounded p-4 cursor-pointer', {
                  'bg-gray-100 ': showTypeMenu,
                })}
                onClick={(e) => {
                  setShowTypeMenu((p) => !p);
                  e.stopPropagation();
                }}
              >
                <FieldIcon size={20} />
              </div>
              <input
                ref={labelInputRef}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Property label"
                className="py-6 px-12 bg-gray-100 border rounded flex-grow"
              />
            </form>
          </div>
          <div className="p-4">
            {showTypeMenu ? (
              <SelectCustomFieldTypeOptions
                selectedType={customField.type}
                onOptionSelect={handleChangeFieldType}
              />
            ) : (
              <EditCustomFieldMenuOptions onOptionSelect={handleBasicMenu} />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SelectCustomFieldTypeOptions = ({
  onOptionSelect,
  selectedType,
}: {
  selectedType?: string;
  onOptionSelect: (type: string) => void;
}) => {
  return (
    <div className="w-full min-w-256">
      {Object.keys(fieldOptionLabels).map((op) => {
        const FieldIcon = getFieldIcon(op);
        const label = fieldOptionLabels[op];
        const isSelected = selectedType === op;
        return (
          <div
            className={cn(
              'flex items-center gap-8 px-12 py-4 text-gray-999 cursor-pointer hover:bg-gray-100 rounded-md',
              {
                'bg-gray-100': isSelected,
              }
            )}
            onClick={() => onOptionSelect(op)}
            key={op}
          >
            <FieldIcon />
            {label}
          </div>
        );
      })}
    </div>
  );
};

const EditCustomFieldMenuOptions = ({
  options = [
    {
      label: 'Duplicate property',
      value: 'duplicate',
      icon: DuplicateIcon,
    },
    {
      label: 'Delete property',
      value: 'delete',
      icon: DeleteIcon,
    },
  ],
  onOptionSelect,
}: {
  options?: { label: string; icon: any; value: string }[];
  onOptionSelect: (type: string) => void;
}) => {
  return (
    <div className="w-full min-w-256">
      {options.map((op) => {
        const { label, value, icon: Icon } = op;
        return (
          <div
            className={cn(
              'flex items-center gap-8 px-12 py-4 text-gray-999 cursor-pointer hover:bg-gray-100 rounded-md text-sm'
            )}
            onClick={() => onOptionSelect(value)}
            key={value}
          >
            <Icon />
            {label}
          </div>
        );
      })}
    </div>
  );
};
